import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSnackbar } from '../../redux/messageSlice';
import {
  getUserProductAccessApi,
  searchProductAccessUsersApi,
  updateProductAccessApi,
} from '../../services/api';

const DEFAULT_SEARCH_ERROR = 'Failed to load people';
const DEFAULT_UPDATE_ERROR = 'Failed to update product access';

// `${userId}:${productKey}` — one admin flips one switch at a time, so a single
// string keeps "is this switch busy" a plain equality check in the row.
export const pendingKeyFor = (userId, productKey) => `${userId}:${productKey}`;

/**
 * Data layer for the Product Access page.
 *
 *   GET  /api/v1/product_accesses/search_users?query=
 *   GET  /api/v1/product_accesses/user_access?user_id=
 *   PUT  /api/v1/product_accesses/update_access
 *
 * All three are Admin-only; every failure carries a user-facing `message` in the
 * standard envelope, so errors surface that message verbatim.
 */
const useProductAccessActions = () => {
  const dispatch = useDispatch();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [pendingKey, setPendingKey] = useState(null);

  const searchUsers = useCallback(
    async ({ query } = {}) => {
      // A blank query is a 400 by design — show the idle state instead of calling.
      if (!query || !query.trim()) {
        setUsers([]);
        setIsError(false);
        setHasLoaded(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setIsError(false);
      try {
        const response = await searchProductAccessUsersApi({ query: query.trim() });
        if (response?.data?.http_code === 200) {
          setUsers(response.data.data?.users || []);
        }
      } catch (error) {
        setUsers([]);
        setIsError(true);
        const message = error?.response?.data?.message || DEFAULT_SEARCH_ERROR;
        dispatch(setSnackbar({ flag: true, type: 'error', message }));
      } finally {
        setIsLoading(false);
        setHasLoaded(true);
      }
    },
    [dispatch]
  );

  // Re-reads one row — after a write, or after a stale 422 so the admin ends up
  // looking at the truth rather than at their own rejected click.
  const refreshUser = useCallback(async (userId) => {
    try {
      const response = await getUserProductAccessApi({ user_id: userId });
      if (response?.data?.http_code === 200 && response.data.data) {
        const fresh = response.data.data;
        setUsers((prev) => prev.map((user) => (user.id === fresh.id ? fresh : user)));
      }
    } catch (error) {
      // A failed refresh leaves the previous row in place; the write's own error
      // snackbar has already told the admin what happened.
    }
  }, []);

  // Optimistic write of the user switch only; the organisation gate is never
  // guessed at, because a gate flip restages every other row of that company.
  const applyUserAccess = useCallback((userId, productKey, access) => {
    setUsers((prev) =>
      prev.map((user) =>
        user.id === userId
          ? {
              ...user,
              products: (user.products || []).map((product) =>
                product.key === productKey ? { ...product, user_access: access } : product
              ),
            }
          : user
      )
    );
  }, []);

  /**
   * One PUT. Resolves `{ success, data, message, orgGateEnabled }` so the caller
   * can decide whether to refetch the whole list (a gate flip makes other rows
   * of the same organisation stale) and never throws.
   */
  const setProductAccess = useCallback(
    async ({ userId, productKey, access }) => {
      setPendingKey(pendingKeyFor(userId, productKey));
      applyUserAccess(userId, productKey, access);
      try {
        const response = await updateProductAccessApi({
          user_id: userId,
          product_key: productKey,
          access,
        });
        const payload = response?.data;
        if (payload?.http_code === 200) {
          dispatch(setSnackbar({ flag: true, type: 'success', message: payload.message }));
          await refreshUser(userId);
          return {
            success: true,
            data: payload.data,
            message: payload.message,
            orgGateEnabled: Boolean(payload.data?.org_gate_enabled),
          };
        }
        applyUserAccess(userId, productKey, !access);
        return { success: false, message: payload?.message };
      } catch (error) {
        const status = error?.response?.status;
        const message = error?.response?.data?.message || DEFAULT_UPDATE_ERROR;
        applyUserAccess(userId, productKey, !access);
        dispatch(setSnackbar({ flag: true, type: 'error', message }));
        // 422 means the row was stale — someone else already changed it.
        if (status === 422) await refreshUser(userId);
        return { success: false, message, status };
      } finally {
        setPendingKey(null);
      }
    },
    [dispatch, refreshUser, applyUserAccess]
  );

  return {
    searchUsers,
    setProductAccess,
    refreshUser,
    users,
    isLoading,
    isError,
    hasLoaded,
    pendingKey,
  };
};

export default useProductAccessActions;
